import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../../service/AuthService";

// 개인정보 처리화면 보호:
//   1) 우클릭(컨텍스트 메뉴) / 텍스트 선택·복사 / 드래그 / 단축키(복사/저장/소스보기) 차단
//   2) 미동작 시 자동 로그아웃 (기본 10분)
//
// CommonContentWrapper(인증 게이트) 안에서 사용된다. 비인증 페이지에는 적용되지 않는다.

const DEFAULT_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30분

const ACTIVITY_EVENTS = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "wheel"];

const isFormElement = (target) => {
    if (!target) return false;
    const tag = target.tagName;
    if (!tag) return false;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    return target.isContentEditable === true;
};

const blockContextMenu = (e) => {
    e.preventDefault();
};

const blockSelection = (e) => {
    if (isFormElement(e.target)) return;
    e.preventDefault();
};

const blockCopy = (e) => {
    if (isFormElement(e.target)) return;
    e.preventDefault();
};

const blockKeyShortcuts = (e) => {
    if (isFormElement(e.target)) return;
    const key = (e.key || "").toLowerCase();
    const ctrlOrMeta = e.ctrlKey || e.metaKey;
    // Ctrl/Cmd + (C/X/A/S/P/U) 차단 — 복사/잘라내기/전체선택/저장/인쇄/소스보기
    if (ctrlOrMeta && ["c", "x", "a", "s", "p", "u"].includes(key)) {
        e.preventDefault();
    }
    // F12 (개발자 도구) — 완벽한 차단은 아니지만 가이드 수준의 통제
    if (key === "f12") {
        e.preventDefault();
    }
};

export function usePrivacyGuard({ idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS } = {}) {
    const navigate = useNavigate();
    const timerRef = useRef(null);

    useEffect(() => {
        document.addEventListener("contextmenu", blockContextMenu);
        document.addEventListener("selectstart", blockSelection);
        document.addEventListener("dragstart", blockSelection);
        document.addEventListener("copy", blockCopy);
        document.addEventListener("cut", blockCopy);
        document.addEventListener("keydown", blockKeyShortcuts);

        return () => {
            document.removeEventListener("contextmenu", blockContextMenu);
            document.removeEventListener("selectstart", blockSelection);
            document.removeEventListener("dragstart", blockSelection);
            document.removeEventListener("copy", blockCopy);
            document.removeEventListener("cut", blockCopy);
            document.removeEventListener("keydown", blockKeyShortcuts);
        };
    }, []);

    useEffect(() => {
        const triggerLogout = async () => {
            try {
                const service = new AuthService();
                await service.logout();
            } catch (e) {
                // 세션 정리 실패 시에도 로그인 화면으로 보낸다.
                console.warn("Idle logout - logout call failed:", e?.message);
            }
            navigate("/auth");
        };

        const reset = () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(triggerLogout, idleTimeoutMs);
        };

        ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, reset, { passive: true }));
        reset();

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, reset));
        };
    }, [idleTimeoutMs, navigate]);
}
